import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useI18n } from '../../i18n';
import { api } from '../../services/api';
import { setPageMeta } from './pageMeta';

export default function PublicStylePage() {
  const { slug } = useParams();
  const { formatDate, t } = useI18n();
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError('');
    api.getPublicSeoCelebrity(slug)
      .then((response) => {
        if (mounted) setPayload(response.data || null);
      })
      .catch((err) => {
        if (mounted) setError(err.message || 'request_failed');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [slug]);

  const celebrity = payload?.celebrity;
  const events = useMemo(() => payload?.events || [], [payload?.events]);

  useEffect(() => {
    if (!celebrity?.name) return;
    setPageMeta({
      title: `${t('stylePage.metaTitle', { name: celebrity.name })} | Outfit Detective`,
      description: t('stylePage.metaDescription', { name: celebrity.name }),
      pathname: `/style/${celebrity.slug}`,
      image: celebrity.image_url,
    });
  }, [celebrity, t]);

  if (loading) {
    return (
      <main className="ct-public-tryon-page">
        <div className="ct-card ct-empty">{t('common.loading')}</div>
      </main>
    );
  }

  if (error || !celebrity) {
    return (
      <main className="ct-public-tryon-page">
        <section className="ct-public-tryon-missing">
          <img src="/brand/outfit-detective-mark-transparent.svg" alt="" />
          <h1>{t('stylePage.missingTitle')}</h1>
          <p>{t('stylePage.missingCopy')}</p>
          <Link to="/" className="btn btn-dark">{t('stylePage.missingCta')}</Link>
        </section>
      </main>
    );
  }

  return (
    <main className="ct-public-tryon-page">
      <section className="ct-public-tryon-shell">
        <article className="ct-public-style-hero">
          {celebrity.image_url ? <img src={celebrity.image_url} alt="" /> : null}
          <div>
            <p className="ct-kicker">{t('stylePage.kicker')}</p>
            <h1>{t('stylePage.title', { name: celebrity.name })}</h1>
            <p>{t('stylePage.copy', { name: celebrity.name })}</p>
          </div>
        </article>

        <section className="ct-public-look-grid">
          {events.map((event) => (
            <article className="ct-public-look-tile" key={event.id}>
              <Link to={`/events/${event.id}`} className="ct-public-look-tile-main">
                {event.image_url ? <img src={event.image_url} alt="" /> : null}
                <div>
                  <span>{event.source_name || t('stylePage.socialFallback')} · {formatDate(event.event_at, { dateStyle: 'medium' })}</span>
                  <h2>{event.title || t('stylePage.lookFallback', { name: celebrity.name })}</h2>
                </div>
              </Link>
              {Number(event.candidate_count || 0) > 0 ? (
                <Link to={`/looks/${event.id}`} className="ct-public-look-shop-link">{t('stylePage.shopSimilar')}</Link>
              ) : (
                <em>{t('stylePage.pending')}</em>
              )}
            </article>
          ))}
        </section>

        <section className="ct-public-faq">
          <h2>{t('stylePage.faqTitle', { name: celebrity.name })}</h2>
          <details>
            <summary>{t('stylePage.faqExactQuestion', { name: celebrity.name })}</summary>
            <p>{t('stylePage.faqExactAnswer')}</p>
          </details>
          <details>
            <summary>{t('stylePage.faqInspirationQuestion')}</summary>
            <p>{t('stylePage.faqInspirationAnswer')}</p>
          </details>
        </section>
      </section>
    </main>
  );
}
